const EXPEDIENT_TABLE_PREFIX = "expedient-field:";

/**
 * Builds the expedient display label the same way the form preview does
 * (see mapExpedientItemToOption): "{unique_code} - {name}", falling back to
 * whichever part exists, and finally to the raw stored value (the UUID) when
 * the backend hasn't resolved the record. See JER-446.
 */
export function formatExpedientLabel(
  uniqueCode: unknown,
  name: unknown,
  fallback: unknown,
): string {
  const code = typeof uniqueCode === "string" ? uniqueCode : "";
  const displayName = typeof name === "string" ? name : "";
  const fb =
    typeof fallback === "string" || typeof fallback === "number"
      ? String(fallback)
      : "";
  if (code && displayName) return `${code} - ${displayName}`;
  return code || displayName || fb;
}

/**
 * Creates a standardized table name for expedient fields
 * Example: createExpedientDataTableName("field-123") returns "expedient-field:field-123"
 */
export const createExpedientDataTableName = (fieldId: string) =>
  `${EXPEDIENT_TABLE_PREFIX}${fieldId}`;

/**
 * Transforms an expedient field into a select field with database options
 * This allows the form engine to fetch expedient data through the external data provider
 */
function applyExpedientTransform<
  TField extends { id: string; type: string; config?: unknown },
>(field: TField): TField {
  if (field.type !== "expedient") return field;
  return {
    ...field,
    type: "select",
    config: {
      ...(field.config && typeof field.config === "object" ? field.config : {}),
      options_type: "database",
      source: "EXPEDIENT",
      data_center_table: createExpedientDataTableName(field.id),
      data_center_column: "label",
    },
  };
}

/**
 * Transforms expedient fields in an array to database-backed select fields
 *
 * This is critical for the form engine to recognize which fields need external data fetching
 */
export function transformFieldsForExpedient<
  TField extends { id: string; type: string; config?: unknown },
>(fields: TField[]): TField[] {
  return fields.map((field) => applyExpedientTransform(field));
}

function isDateRangeValue(
  value: unknown,
): value is { start: unknown; end: unknown } {
  return (
    typeof value === "object" &&
    value !== null &&
    "start" in value &&
    "end" in value
  );
}

export function shouldIncludeAutosaveField(
  key: string,
  value: unknown,
  dateFieldNames: Set<string>,
): boolean {
  if (
    typeof value === "object" &&
    value !== null &&
    Object.keys(value).length > 0 &&
    Object.values(value as Record<string, unknown>).some((field) => !field)
  ) {
    return false;
  }
  if (value === null || value === undefined || value === "") return false;
  // An empty is_array field (e.g. multi-value text/number/email) has no
  // user input yet — excluding it prevents autosave from overwriting
  // previously saved values with [].
  if (Array.isArray(value) && value.length === 0) return false;
  if (!dateFieldNames.has(key)) return true;
  if (typeof value === "string") return value.trim().length > 0;
  if (isDateRangeValue(value)) return !!value.start && !!value.end;
  return true;
}

// Scalar field types serialized as a flat array of strings when is_array.
// Object-valued array types (date_range, select, etc.) are excluded so their
// shape is preserved. Phone is handled separately (see
// serializeMultiValuePhoneFields) because each entry must be combined into a
// single "+<calling_code><number>" string.
const MULTI_VALUE_STRING_TYPES = new Set([
  "text",
  "textarea",
  "number",
  "email",
]);

const unwrapMultiValueItemToString = (item: unknown): string => {
  // The form engine stores each is_array item as { value: ... }; read the flat
  // value (or the item itself) and coerce primitives to string.
  const value =
    item && typeof item === "object" && "value" in item ? item.value : item;
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

/**
 * Serializes multi-value (is_array) text/textarea/number/email fields into the shape
 * the backend expects: a flat array of strings, e.g. ["2", "46"]. The form
 * engine emits each item as { value: x }, so we unwrap and coerce to string.
 * Empty entries (e.g. an "Agregue un valor" row the user hasn't filled yet)
 * are dropped — the backend rejects empty strings as invalid array items.
 * Single-value fields and object-valued array types are left untouched.
 */
export function stringifyMultiValueFields<
  TField extends { name: string; type: string; is_array?: boolean },
>(
  fields: Record<string, unknown>,
  allFields: TField[],
): Record<string, unknown> {
  const targetNames = new Set(
    allFields
      .filter(
        (field) => field.is_array && MULTI_VALUE_STRING_TYPES.has(field.type),
      )
      .map((field) => field.name),
  );
  if (targetNames.size === 0) return fields;

  const result: Record<string, unknown> = { ...fields };
  targetNames.forEach((name) => {
    const value = result[name];
    if (Array.isArray(value)) {
      result[name] = value
        .map(unwrapMultiValueItemToString)
        .filter((v) => v !== "");
    }
  });

  return result;
}

// El Salvador. Used when the country selector is disabled (allow_extensions
// is false), where the form engine fixes the country and omits the calling code.
const DEFAULT_PHONE_CALLING_CODE = "503";

const toDigits = (value: unknown): string =>
  typeof value === "string" || typeof value === "number"
    ? String(value).replace(/\D/g, "")
    : "";

const buildInternationalPhone = (national: string, callingCode: string) =>
  national ? `+${callingCode || DEFAULT_PHONE_CALLING_CODE}${national}` : "";

/**
 * Builds the "+<calling_code><national_number>" string the backend expects for
 * a single is_array phone entry. The form engine emits each item as either:
 *   - { value: "70000000" }                                  (no country selector)
 *   - { value: { [name]: "70000000",
 *                [name]_extension: "SV",
 *                [name]_calling_code: "503" } }              (country selector)
 * Returns "" when the entry has no number so callers can drop empty entries.
 */
const serializePhoneItem = (item: unknown, fieldName: string): string => {
  const inner =
    item && typeof item === "object" && "value" in item ? item.value : item;

  if (inner && typeof inner === "object") {
    const obj = inner as Record<string, unknown>;
    return buildInternationalPhone(
      toDigits(obj[fieldName]),
      toDigits(obj[`${fieldName}_calling_code`]),
    );
  }

  return buildInternationalPhone(toDigits(inner), DEFAULT_PHONE_CALLING_CODE);
};

/**
 * Serializes multi-value (is_array) phone fields into the shape the backend
 * expects: a flat array of full international numbers, e.g.
 * ["+50312345678", "+50387654321"]. Empty entries are dropped. Single-value
 * phone fields keep their existing shape (separate "<name>",
 * "<name>_extension" and "<name>_calling_code" keys) and are left untouched.
 */
export function serializeMultiValuePhoneFields<
  TField extends { name: string; type: string; is_array?: boolean },
>(
  fields: Record<string, unknown>,
  allFields: TField[],
): Record<string, unknown> {
  const targetNames = new Set(
    allFields
      .filter((field) => field.is_array && field.type === "phone")
      .map((field) => field.name),
  );
  if (targetNames.size === 0) return fields;

  const result: Record<string, unknown> = { ...fields };
  targetNames.forEach((name) => {
    const value = result[name];
    if (Array.isArray(value)) {
      result[name] = value
        .map((item) => serializePhoneItem(item, name))
        .filter((phone) => phone !== "");
    }
  });

  return result;
}
